import i18n from '@/utils/i18n';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  ChatDetails_ChatMessageEdgeFragment,
  ChatDetails_ChatMessageFragment,
  ChatDetails_ChatMessagesQuery,
  ChatDetails_ChatMessagesQueryVariables,
  ChatDetails_MessagesSubscription,
  ChatDetails_MessagesSubscriptionVariables,
  ChatDetails_NewChatMessagesQuery,
  ChatDetails_NewChatMessagesQueryVariables,
} from '@/types';
import { gql } from '@apollo/client';
import { Message } from '@/components/chats/types';
import { MessageType } from '@/enums';
import { useAuthenticatedUserContext } from '@/contexts/user-context';
import { useSubscription } from '@/contexts/subscription-context';
import { useWrappedQuery } from '@/hooks/use-wrapped-query';

export const DEFAULT_LIMIT = 20;

export const adaptChatMessage = (
  message: ChatDetails_ChatMessageFragment,
): Message => {
  const adaptedMessage: Message = {
    _id: message.uuid,
    createdAt: new Date(message.sent_at),
    creditRequestBatch:
      message.credit_request_batches_messages &&
      message.credit_request_batches_messages.length
        ? message.credit_request_batches_messages[0].credit_request_batch
        : null,
    error: null,
    orderInvoice: message.order_invoice,
    orderStatusUpdate: message.order_status_update,
    rawCreatedAt: message.sent_at,
    system: message.type === MessageType.SYSTEM_MESSAGE,
    text: message.order_invoice
      ? i18n.t('invoice_number', {
          number:
            message.order_invoice.invoice_number ||
            message.order_invoice.order?.customer_facing_id ||
            '',
        })
      : message.text_content || '',
    user: {
      _id: message.sent_by_uuid,
      name: message.sent_by_name ?? undefined,
    },
  };

  if (message.media_content_url) {
    adaptedMessage.image = message.media_content_url;
  }

  return adaptedMessage;
};

const adaptChatMessages = (
  edges: ChatDetails_ChatMessageEdgeFragment[],
): Message[] => {
  return edges.reduce<Message[]>((acc, edge) => {
    const message = edge.node;

    // Skip PLAINTEXT messages without text content
    if (
      message.type !== MessageType.PLAINTEXT ||
      message.text_content?.trim()
    ) {
      acc.push(adaptChatMessage(message));
    }

    return acc;
  }, []);
};

const MESSAGE_ORDER_STATUS_UPDATE_FRAGMENT = gql`
  fragment ChatDetails_ChatMessageOrderStatusUpdate on order_status_updates {
    order {
      customer_facing_id
      fulfillment_type
      uuid
    }
    status
    uuid
  }
`;

const MESSAGE_ORDER_INVOICE_FRAGMENT = gql`
  fragment ChatDetails_ChatMessageOrderInvoice on order_invoices {
    invoice_number
    order {
      customer_facing_id
      uuid
    }
    uuid
  }
`;

const MESSAGE_CREDIT_REQUEST_FRAGMENT = gql`
  fragment ChatDetails_ChatMessageCreditRequestMessage on credit_request_batches_messages {
    credit_request_batch {
      uuid
    }
    uuid
  }
`;

export const MESSAGE_FRAGMENT = gql`
  fragment ChatDetails_ChatMessage on messages {
    media_content_url
    order_invoice {
      ...ChatDetails_ChatMessageOrderInvoice
    }
    order_status_update {
      ...ChatDetails_ChatMessageOrderStatusUpdate
    }
    credit_request_batches_messages(limit: 1) {
      ...ChatDetails_ChatMessageCreditRequestMessage
    }
    sent_at
    sent_by_name
    sent_by_uuid
    text_content
    type
    uuid
  }

  ${MESSAGE_ORDER_INVOICE_FRAGMENT}
  ${MESSAGE_ORDER_STATUS_UPDATE_FRAGMENT}
  ${MESSAGE_CREDIT_REQUEST_FRAGMENT}
`;

const MESSAGE_EDGE_FRAGMENT = gql`
  fragment ChatDetails_ChatMessageEdge on messagesEdge {
    cursor
    node {
      ...ChatDetails_ChatMessage
    }
  }

  ${MESSAGE_FRAGMENT}
`;

export const CHAT_MESSAGES_QUERY = gql`
  query ChatDetails_ChatMessages(
    $after: String
    $chatUUID: uuid!
    $limit: Int
  ) {
    chats_connection(where: { uuid: { _eq: $chatUUID } }) {
      edges {
        node {
          supplier {
            name
            uuid
          }
          uuid
        }
      }
    }

    messages_connection(
      after: $after
      first: $limit
      order_by: { sent_at: desc }
      where: { chat_uuid: { _eq: $chatUUID } }
    ) {
      edges {
        ...ChatDetails_ChatMessageEdge
      }
      pageInfo {
        # Hasura returns null if there is no endCursor but the introspected
        # schema defines it as non-nullable so we use the edge cursor instead
        # https://github.com/hasura/graphql-engine/issues/5019
        # endCursor
        hasNextPage
      }
    }
  }

  ${MESSAGE_EDGE_FRAGMENT}
`;

export const CHAT_LAST_MESSAGE_SUBSCRIPTION = gql`
  subscription ChatDetails_Messages(
    $chatUUID: uuid!
    $now: timestamptz!
    $userUUID: uuid!
  ) {
    chat_last_message(
      where: {
        chat_uuid: { _eq: $chatUUID }
        employee_uuid: { _eq: $userUUID }
        last_message_sent_at: { _gt: $now }
      }
    ) {
      last_message_uuid
    }
  }
`;

// Define a separate query since Hasura doesn't allow a query that specifies
// both `after` and `before` (even if they don't have a value)
export const NEW_CHAT_MESSAGES_QUERY = gql`
  query ChatDetails_NewChatMessages($before: String, $chatUUID: uuid!) {
    messages_connection(
      before: $before
      order_by: { sent_at: desc }
      where: { chat_uuid: { _eq: $chatUUID } }
    ) {
      edges {
        ...ChatDetails_ChatMessageEdge
      }
    }
  }

  ${MESSAGE_EDGE_FRAGMENT}
`;

type ChatMessagesState = Message[] | null;

interface Params {
  chatUUID: string;
  isFocused: boolean;
}

export const useGetChatMessages = ({ chatUUID, isFocused }: Params) => {
  const { user } = useAuthenticatedUserContext();

  const [chatMessages, _setChatMessages] = useState<ChatMessagesState>(null);
  const [fetchingMore, setFetchingMore] = useState(false);

  // Use refs since components don't render these values
  const fetchingNewRef = useRef(false);
  const hasInitialFirstCursorRef = useRef(false);
  const nowRef = useRef(new Date());

  const [firstCursor, setFirstCursor] = useState<string | null>(null);
  const [lastCursor, setLastCursor] = useState<string | null>(null);

  const { client, data, error, hasResponse, loading, networkStatus } =
    useWrappedQuery<
      ChatDetails_ChatMessagesQuery,
      ChatDetails_ChatMessagesQueryVariables
    >(CHAT_MESSAGES_QUERY, {
      context: {
        relay: true,
      },
      fetchPolicy: 'network-only',
      notifyOnNetworkStatusChange: true,
      variables: {
        chatUUID,
        limit: DEFAULT_LIMIT,
      },
    });

  const setChatMessages: React.Dispatch<
    React.SetStateAction<ChatMessagesState>
  > = useCallback(
    (
      nextState:
        | ChatMessagesState
        | ((previousState: ChatMessagesState) => ChatMessagesState),
    ) => {
      _setChatMessages((previousChatMessages) => {
        const nextChatMessages =
          typeof nextState === 'function'
            ? nextState(previousChatMessages)
            : nextState;
        if (!nextChatMessages) {
          return previousChatMessages;
        }

        const messageUUIDs = new Set();
        return nextChatMessages
          .filter(({ _id }) => !messageUUIDs.has(_id) && messageUUIDs.add(_id))
          .sort((a, b) => {
            // @ts-ignore - TypeScript complains about comparing dates
            return b.createdAt - a.createdAt;
          });
      });
    },
    [],
  );

  const fetchNewMessages = useCallback(async () => {
    if (fetchingNewRef.current) {
      return;
    }

    fetchingNewRef.current = true;

    try {
      const { data: newData } = await client.query<
        ChatDetails_NewChatMessagesQuery,
        ChatDetails_NewChatMessagesQueryVariables
      >({
        context: {
          relay: true,
        },
        fetchPolicy: 'network-only',
        query: NEW_CHAT_MESSAGES_QUERY,
        variables: {
          before: lastCursor,
          chatUUID,
        },
      });

      const { edges = [] } = newData?.messages_connection || {};
      setChatMessages((previousChatMessages) => {
        const nextChatMessages = adaptChatMessages(edges);
        return [...nextChatMessages, ...(previousChatMessages || [])];
      });

      setLastCursor(edges[0]?.cursor || null);
    } catch (e) {
      // Do nothing
    }

    fetchingNewRef.current = false;
  }, [client, lastCursor, chatUUID, setChatMessages]);

  useSubscription<
    ChatDetails_MessagesSubscription,
    ChatDetails_MessagesSubscriptionVariables
  >(CHAT_LAST_MESSAGE_SUBSCRIPTION, {
    fetchPolicy: 'no-cache',
    onData: async ({ data: subscriptionData }) => {
      // onData is called without a message when it initially loads, so check that there's
      // a message before fetching new ones
      const lastMessageUUID =
        subscriptionData.data?.chat_last_message[0]?.last_message_uuid;
      if (lastMessageUUID) {
        await fetchNewMessages();
      }
    },
    // Subscribe only after the chat details screen is initially focused
    skip: !hasResponse || !isFocused,
    variables: {
      chatUUID,
      now: nowRef.current.toISOString(),
      userUUID: user.uuid,
    },
  });

  useEffect(() => {
    const { edges = [], pageInfo } = data?.messages_connection || {};
    setChatMessages((previousChatMessages) => {
      const nextChatMessages = adaptChatMessages(edges);
      return [...nextChatMessages, ...(previousChatMessages || [])];
    });

    // This check prevents firstCursor from being reset after loading earlier
    // messages since this hook runs every time the query is updated
    const cursor =
      (pageInfo?.hasNextPage && edges[edges.length - 1]?.cursor) || null;
    if (cursor && !hasInitialFirstCursorRef.current) {
      setFirstCursor(cursor);
      hasInitialFirstCursorRef.current = true;
    }

    setLastCursor(edges[0]?.cursor || null);
  }, [data, setChatMessages]);

  const fetchMore = useCallback(async () => {
    setFetchingMore(true);

    try {
      const { data: fetchMoreData } = await client.query<
        ChatDetails_ChatMessagesQuery,
        ChatDetails_ChatMessagesQueryVariables
      >({
        context: {
          relay: true,
        },
        query: CHAT_MESSAGES_QUERY,
        variables: {
          after: firstCursor,
          chatUUID,
          limit: DEFAULT_LIMIT,
        },
      });

      const { edges = [], pageInfo } = fetchMoreData?.messages_connection || {};
      setChatMessages((previousChatMessages) => {
        const nextChatMessages = adaptChatMessages(edges);
        return [...(previousChatMessages || []), ...nextChatMessages];
      });

      setFirstCursor(
        (pageInfo.hasNextPage && edges[edges.length - 1]?.cursor) || null,
      );
    } catch (e) {
      // Do nothing
    }

    setFetchingMore(false);
  }, [chatUUID, client, firstCursor, setChatMessages]);

  return {
    chatMessages,
    error,
    fetchingMore,
    fetchMore,
    firstCursor,
    hasEarlierMessages: Boolean(firstCursor),
    hasResponse,
    lastCursor,
    loading,
    networkStatus,
    setChatMessages,
  };
};
